/**
 * Which installer a run uses, when the run may choose (IM-197).
 *
 * The release pipeline builds the `qrvey-terraform` installer and the chart
 * from one version (IM-196), so a run is asked for one **Qrvey version** and
 * that sets both. Until dispatcher v16 the installer was fixed per region by
 * the stack's `RunnerImage`, so moving one instance to a new installer meant
 * moving every instance in that region — and on a test account shared with
 * another team, that team's change of a region's image moved everyone's.
 *
 * ## A tag, never an image
 *
 * A run names a **tag**; the repository is always the region's own
 * `RunnerImage`. That keeps the rule that the control plane may ask for a run
 * but cannot choose the code that runs: every tag in that repository is
 * Qrvey's installer, while a free-form image would let a compromised control
 * plane run any container under the deploy role. The dispatcher holds the
 * same pattern (`INSTALLER_TAG_PATTERN`), and a test ties the two.
 *
 * ## The same for every account
 *
 * Decided with Amit (2026-10-09): a run uses the region's image unless the
 * person starting it overrides it, customer or Qrvey alike, so nothing here
 * goes through `policyFor`. `RunImageTag=fixed` is the customer's opt-out, in
 * their own stack.
 *
 * ## Known without reading the account
 *
 * The start path does not read the account (CLAUDE.md, *Preflight*), so what
 * a region allows is what a read last **observed** — the onboarding screen or
 * a preflight, which already read each dispatcher stack — exactly as IM-194
 * keeps the boundary mode. Never observed is `unknown`, and a run that names a
 * tag there is refused with a sentence saying how to find out: an older
 * dispatcher would refuse the field anyway, onto a run record, as
 * `unexpected-fields`.
 *
 * ## Never below 9.4
 *
 * A tag whose release line is older than `MINIMUM_QRVEY_CHART_VERSION` is
 * refused before the region is consulted, even when it is the region's own:
 * such a build cannot run at all (`installerVersionProblem`). Before this, a
 * plan and an output against an adopted 9.3 instance chose its 9.3 tag through
 * the run form's one Qrvey version field and died in the wrapper with exit 127.
 */
import { CHOSEN_IMAGE_DISPATCHER_VERSION } from '../bootstrap/dispatcher-template.js';
import { isRunnerImageAddress } from '../bootstrap/runner-image.js';
import { installerVersionProblem } from './config.js';
import { installerTag } from './installer-match.js';
/**
 * Docker's tag grammar, as the dispatcher holds it: a word character, then up
 * to 127 of word characters, `.` and `-`. So no `/`, `@` or `:` — nothing that
 * could reach another repository, a digest, or a second tag.
 */
export const INSTALLER_TAG_PATTERN = '^\\w[\\w.-]{0,127}$';
const INSTALLER_TAG_SHAPE = new RegExp(INSTALLER_TAG_PATTERN);
/** Why a value cannot be a run's installer tag, or undefined when it can. */
export const installerTagProblem = (tag) => {
    if (typeof tag !== 'string')
        return 'installerTag must be a string';
    if (INSTALLER_TAG_SHAPE.test(tag))
        return undefined;
    return `installerTag must be an image tag such as 9.6-20261007-3555 — letters, digits, '_', '.' and '-', at most 128 — not ${JSON.stringify(tag)}. The repository is always the region's own RunnerImage, so a '/', '@' or ':' is never accepted.`;
};
const atLeast = (version, floor) => {
    const v = /^\d+$/.test(version.trim()) ? Number.parseInt(version.trim(), 10) : undefined;
    return v !== undefined && v >= Number.parseInt(floor, 10);
};
/**
 * A region's choice, from what its dispatcher stack says: the
 * `DispatcherVersion` output and the `RunImageTag` parameter.
 *
 * A v16 stack whose mode reads as anything but `allowed` is `fixed`, as the
 * dispatcher itself treats it — failing closed in the same direction.
 */
export const installerChoiceOf = (facts) => {
    const version = facts.dispatcherVersion;
    if (facts.dispatcherUnreadable === true || version === undefined)
        return { kind: 'unknown' };
    if (!/^\d+$/.test(version.trim()))
        return { kind: 'unknown' };
    if (!atLeast(version, CHOSEN_IMAGE_DISPATCHER_VERSION)) {
        return { kind: 'too-old', dispatcherVersion: version };
    }
    return facts.runImageTag === 'allowed' ? { kind: 'allowed' } : { kind: 'fixed' };
};
/**
 * The installer one run will use (IM-197).
 *
 * `requested` is the tag the run asked for, or undefined when it asked for
 * none. `regionImage` is the region's `RunnerImage`, as last read.
 */
export const runInstaller = (input) => {
    const { requested, regionImage, choice, region } = input;
    const fromRegion = {
        source: 'region',
        ...(regionImage === undefined ? {} : { image: regionImage }),
    };
    if (requested === undefined)
        return fromRegion;
    const problem = installerTagProblem(requested) ?? installerVersionProblem(requested);
    if (problem !== undefined)
        return { ...fromRegion, refusal: problem };
    const regionTag = regionImage === undefined ? undefined : installerTag(regionImage);
    if (requested === regionTag)
        return fromRegion;
    switch (choice.kind) {
        case 'allowed':
            if (regionImage === undefined || !isRunnerImageAddress(regionImage)) {
                return {
                    ...fromRegion,
                    refusal: `The installer image in ${region} is not a registry/repository:tag address, so there is no repository to put tag ${requested} on. Fix the region's RunnerImage first.`,
                };
            }
            return {
                source: 'run',
                image: `${regionImage.slice(0, regionImage.lastIndexOf(':') + 1)}${requested}`,
                tag: requested,
            };
        case 'fixed':
            return {
                ...fromRegion,
                refusal: `${region}'s dispatcher stack is set to RunImageTag=fixed, so every run there uses its RunnerImage${regionTag === undefined ? '' : ` (${regionTag})`}. Start the run on that installer, or ask whoever owns the stack to set RunImageTag to allowed.`,
            };
        case 'too-old':
            return {
                ...fromRegion,
                refusal: `${region}'s dispatcher is v${choice.dispatcherVersion}, and choosing the installer per run needs v${CHOSEN_IMAGE_DISPATCHER_VERSION}. Start the run on the region's installer${regionTag === undefined ? '' : ` (${regionTag})`}, or update the region's dispatcher stack from the account's onboarding screen.`,
            };
        case 'unknown':
            return {
                ...fromRegion,
                refusal: `Whether ${region}'s dispatcher lets a run choose its installer has not been read. Open the account's onboarding screen, or check the run first, then start it again.`,
            };
    }
};
//# sourceMappingURL=run-installer.js.map